<template>
  <div class="admin-page admin-page--graph-manage">
    <header class="admin-page__header">
      <div>
        <h1 class="admin-page__title">图谱管理</h1>
        <p class="admin-page__subtitle">
          创建、重命名与启用状态维护
          <span
            v-if="!state.loading && state.graphList.length"
            class="graph-manage-summary"
          >
            · 共 {{ state.graphList.length }} 个图谱
          </span>
        </p>
      </div>
      <div class="admin-page__actions">
        <el-button type="primary" @click="openCreateDialog">
          <el-icon class="graph-manage-btn-icon"><Plus /></el-icon>
          新建图谱
        </el-button>
      </div>
    </header>

    <section v-loading="state.loading" class="graph-manage-board">
      <el-empty
        v-if="!state.loading && !state.graphList.length"
        description="暂无图谱"
        class="graph-manage-empty"
      >
        <el-button type="primary" @click="openCreateDialog">新建图谱</el-button>
      </el-empty>

      <div v-else class="graph-card-grid">
        <article
          v-for="graph in state.graphList"
          :key="graph.id"
          class="graph-card"
          :class="[
            graph.status === 1 ? 'is-active' : 'is-inactive',
            { 'is-clickable': graph.status === 1 },
          ]"
          @click="onCardClick(graph)"
        >
          <div class="graph-card__shine" aria-hidden="true" />

          <header class="graph-card__header">
            <div class="graph-card__icon" aria-hidden="true">
              <el-icon><DataAnalysis /></el-icon>
            </div>
            <div class="graph-card__title-block">
              <h3 class="graph-card__title" :title="graph.graphName">
                {{ graph.graphName }}
              </h3>
              <span class="graph-card__id">图谱 #{{ graph.id }}</span>
            </div>
            <span class="graph-card__status">
              <span class="graph-card__status-dot" />
              {{ graph.status === 1 ? "启用中" : "已禁用" }}
            </span>
          </header>

          <div class="graph-card__metrics">
            <div class="graph-card__metric graph-card__metric--primary">
              <div class="graph-card__metric-icon">
                <el-icon><Box /></el-icon>
              </div>
              <div class="graph-card__metric-text">
                <span class="graph-card__metric-value">{{
                  formatEntityCount(graph.entityCount)
                }}</span>
                <span class="graph-card__metric-label">实体数量</span>
              </div>
            </div>
            <span v-if="graph.status === 1" class="graph-card__enter-hint">
              点击进入实体管理
              <el-icon><ArrowRight /></el-icon>
            </span>
          </div>

          <footer class="graph-card__footer" @click.stop>
            <el-button
              size="small"
              text
              type="primary"
              @click="openRenameDialog(graph)"
            >
              <el-icon><EditPen /></el-icon>
              重命名
            </el-button>
            <el-button
              v-if="graph.status === 1"
              size="small"
              text
              type="danger"
              @click="confirmDisable(graph)"
            >
              <el-icon><CircleClose /></el-icon>
              禁用
            </el-button>
            <el-button
              v-else
              size="small"
              text
              type="success"
              @click="confirmEnable(graph)"
            >
              <el-icon><CircleCheck /></el-icon>
              启用
            </el-button>
          </footer>
        </article>
      </div>
    </section>

    <el-dialog
      v-model="state.dialogVisible"
      :title="state.dialogMode === 'create' ? '新建图谱' : '重命名图谱'"
      width="420px"
      @closed="resetDialog"
    >
      <el-form
        ref="formRef"
        :model="state.form"
        :rules="formRules"
        label-width="90px"
      >
        <el-form-item label="图谱名称" prop="graphName">
          <el-input
            v-model="state.form.graphName"
            placeholder="请输入图谱名称"
            maxlength="255"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="state.dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="state.submitting" @click="submit">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  ArrowRight,
  Box,
  CircleCheck,
  CircleClose,
  DataAnalysis,
  EditPen,
  Plus,
} from "@element-plus/icons-vue";
import {
  createGraph,
  disableGraph,
  enableGraph,
  listAdminGraphs,
  renameGraph,
} from "@/api/adminGraphApi";
import { showApiErrorMessage } from "@/api/httpService";
import { setGraphId } from "@/composables/useAdminSelectedGraph";

const router = useRouter();
const formRef = ref(null);

const state = reactive({
  loading: false,
  submitting: false,
  graphList: [],
  dialogVisible: false,
  dialogMode: "create",
  editingId: null,
  form: {
    graphName: "",
  },
});

const formRules = {
  graphName: [{ required: true, message: "请输入图谱名称", trigger: "blur" }],
};

function formatEntityCount(count) {
  if (count == null || Number.isNaN(Number(count))) {
    return "—";
  }
  return Number(count).toLocaleString("zh-CN");
}

function onCardClick(graph) {
  if (graph.status !== 1) {
    return;
  }
  setGraphId(graph.id);
  router.push({
    path: "/admin/entities",
    query: { graphId: String(graph.id) },
  });
}

async function loadGraphs() {
  state.loading = true;
  try {
    const res = await listAdminGraphs();
    if (res?.code === 0) {
      state.graphList = res.data || [];
    }
  } finally {
    state.loading = false;
  }
}

function openCreateDialog() {
  state.dialogMode = "create";
  state.editingId = null;
  state.form.graphName = "";
  state.dialogVisible = true;
}

function openRenameDialog(row) {
  state.dialogMode = "rename";
  state.editingId = row.id;
  state.form.graphName = row.graphName;
  state.dialogVisible = true;
}

function resetDialog() {
  state.form.graphName = "";
  state.editingId = null;
}

async function submit() {
  if (!formRef.value) {
    return;
  }
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) {
    return;
  }
  state.submitting = true;
  try {
    const name = state.form.graphName.trim();
    const res =
      state.dialogMode === "create"
        ? await createGraph(name)
        : await renameGraph(state.editingId, name);
    if (res?.code === 0) {
      ElMessage.success(
        state.dialogMode === "create" ? "创建成功" : "重命名成功"
      );
      state.dialogVisible = false;
      await loadGraphs();
    } else {
      showApiErrorMessage(res);
    }
  } finally {
    state.submitting = false;
  }
}

async function confirmDisable(row) {
  try {
    await ElMessageBox.confirm(
      `确定禁用图谱「${row.graphName}」吗？禁用后普通用户将无法选择，MongoDB 数据不会删除。`,
      "禁用图谱",
      { type: "warning" }
    );
    const res = await disableGraph(row.id);
    if (res?.code === 0) {
      ElMessage.success("已禁用");
      await loadGraphs();
    } else {
      showApiErrorMessage(res);
    }
  } catch {
    // 用户取消
  }
}

async function confirmEnable(row) {
  try {
    await ElMessageBox.confirm(
      `确定启用图谱「${row.graphName}」吗？启用后普通用户可再次选择该图谱。`,
      "启用图谱",
      { type: "info" }
    );
    const res = await enableGraph(row.id);
    if (res?.code === 0) {
      ElMessage.success("已启用");
      await loadGraphs();
    } else {
      showApiErrorMessage(res);
    }
  } catch {
    // 用户取消
  }
}

onMounted(() => {
  loadGraphs();
});
</script>
